/**
 * Balance Calculator Utility
 * 
 * Calculates current account balance from opening balance and transactions
 */

import type { Transaction } from '../types/transaction.types';
import { AccountType } from '../types/account.types';

/**
 * Calculate current balance for an account
 * 
 * Formula: openingBalance + sum(credits) - sum(debits)
 * 
 * For credit cards and loans:
 * - Debits increase the debt (balance becomes more negative)
 * - Credits decrease the debt (balance becomes less negative)
 * - Opening balance is typically 0 or negative (representing existing debt)
 * 
 * @param openingBalance - The account's opening balance
 * @param openingDate - The account's opening date (ISO date string)
 * @param transactions - Array of transactions for the account
 * @param accountType - Optional account type for special handling
 * @param accountId - The account ID being calculated for (required for transfer handling)
 * @returns Calculated current balance
 */
export function calculateCurrentBalance(
  openingBalance: number,
  openingDate: string,
  transactions: Transaction[],
  _accountType?: AccountType,
  accountId?: string
): number {
  // If no transactions, return opening balance
  if (!transactions || transactions.length === 0) {
    return openingBalance || 0;
  }

  // Filter transactions on or after opening date and with valid status
  const openingDateObj = new Date(openingDate);
  // Normalize dates to midnight for accurate comparison
  openingDateObj.setHours(0, 0, 0, 0);
  
  const relevantTransactions = transactions.filter((transaction) => {
    const transactionDate = new Date(transaction.date);
    transactionDate.setHours(0, 0, 0, 0);
    const isAfterOpeningDate = transactionDate >= openingDateObj;
    // Only include completed and pending transactions (exclude needs_review)
    const isValidStatus = transaction.status === 'completed' || transaction.status === 'pending';
    return isAfterOpeningDate && isValidStatus;
  });


  // Calculate sum of credits and debits
  let creditSum = 0;
  let debitSum = 0;

  relevantTransactions.forEach((transaction) => {
    const amount = Math.abs(transaction.amount) || 0;
    
    
    if (transaction.type === 'credit') {
      creditSum += amount;
    } else if (transaction.type === 'debit') {
      debitSum += amount;
    } else if (transaction.type === 'transfer') {
      // For transfers, determine if this account is the source or destination
      // When fetching transactions for an account, we filter by account_id,
      // so transaction.accountId should match the account we're calculating for.
      // However, we also need to check source/destination to determine debit vs credit.
      
      // Use the passed accountId if available, otherwise fall back to transaction.accountId
      const targetAccountId = accountId || transaction.accountId;
      
      if (transaction.sourceAccountId && transaction.destinationAccountId) {
        // If this account is the source, it's a debit (money leaving)
        if (transaction.sourceAccountId === targetAccountId) {
          debitSum += amount;
        }
        // If this account is the destination, it's a credit (money coming in)
        else if (transaction.destinationAccountId === targetAccountId) {
          creditSum += amount;
        }
        // If neither matches, the transaction doesn't belong to this account (shouldn't happen)
        // but we'll log it for debugging
        else {
          console.warn('Transfer transaction does not match source or destination account:', {
            transactionId: transaction.id,
            transactionAccountId: transaction.accountId,
            sourceAccountId: transaction.sourceAccountId,
            destinationAccountId: transaction.destinationAccountId,
            targetAccountId
          });
        }
      }
    }
  });

  // Calculate current balance: openingBalance + credits - debits
  // This formula works for all account types:
  // - Bank/Savings: positive balances, credits increase, debits decrease
  // - Credit Cards/Loans: negative balances, debits increase debt, credits decrease debt
  const calculatedBalance = (openingBalance || 0) + creditSum - debitSum;

  return calculatedBalance;
}

/**
 * Get the most recent month that has transactions
 * 
 * @param transactions - Array of transactions
 * @returns Object with month and year, or null if no transactions
 */
function getMostRecentMonthWithTransactions(transactions: Transaction[]): { month: number; year: number } | null {
  if (!transactions || transactions.length === 0) {
    return null;
  }

  // Find the most recent transaction date
  const dates = transactions.map(t => new Date(t.date));
  const mostRecentDate = new Date(Math.max(...dates.map(d => d.getTime())));

  return {
    month: mostRecentDate.getMonth(),
    year: mostRecentDate.getFullYear()
  };
}

/**
 * Calculate monthly inflow (sum of credits in most recent month with transactions)
 * 
 * @param transactions - Array of transactions
 * @returns Sum of credit transactions in most recent month with transactions
 */
export function calculateMonthlyInflow(transactions: Transaction[]): number {
  if (!transactions || transactions.length === 0) {
    return 0;
  }

  // Get the most recent month that has transactions
  const targetMonth = getMostRecentMonthWithTransactions(transactions);
  if (!targetMonth) {
    return 0;
  }

  return transactions
    .filter((transaction) => {
      const transactionDate = new Date(transaction.date);
      return (
        transaction.type === 'credit' &&
        transactionDate.getMonth() === targetMonth.month &&
        transactionDate.getFullYear() === targetMonth.year
      );
    })
    .reduce((sum, transaction) => sum + (Math.abs(transaction.amount) || 0), 0);
}

/**
 * Calculate monthly outflow (sum of debits in most recent month with transactions)
 * 
 * @param transactions - Array of transactions
 * @returns Sum of debit transactions in most recent month with transactions
 */
export function calculateMonthlyOutflow(transactions: Transaction[]): number {
  if (!transactions || transactions.length === 0) {
    return 0;
  }

  // Get the most recent month that has transactions
  const targetMonth = getMostRecentMonthWithTransactions(transactions);
  if (!targetMonth) {
    return 0;
  }

  return transactions
    .filter((transaction) => {
      const transactionDate = new Date(transaction.date);
      return (
        transaction.type === 'debit' &&
        transactionDate.getMonth() === targetMonth.month &&
        transactionDate.getFullYear() === targetMonth.year
      );
    })
    .reduce((sum, transaction) => sum + (Math.abs(transaction.amount) || 0), 0);
}

/**
 * Calculate total inflow (sum of all credit transactions and incoming transfers)
 * 
 * @param transactions - Array of transactions
 * @param accountId - The account ID being calculated for (required for transfer handling)
 * @param openingDate - Optional opening date to filter transactions (only include transactions on or after this date)
 * @returns Sum of all credit transactions and incoming transfers
 */
export function calculateTotalInflow(transactions: Transaction[], accountId?: string, openingDate?: string): number {
  if (!transactions || transactions.length === 0) {
    return 0;
  }

  // Filter by status (only completed and pending, exclude needs_review)
  let relevantTransactions = transactions.filter(t => 
    t.status === 'completed' || t.status === 'pending'
  );

  // Filter by opening date if provided (only include transactions on or after opening date)
  if (openingDate) {
    const openingDateObj = new Date(openingDate);
    openingDateObj.setHours(0, 0, 0, 0);
    
    relevantTransactions = relevantTransactions.filter((transaction) => {
      const transactionDate = new Date(transaction.date);
      transactionDate.setHours(0, 0, 0, 0);
      return transactionDate >= openingDateObj;
    });
  }

  let total = 0;

  relevantTransactions.forEach((transaction) => {
    const amount = Math.abs(transaction.amount) || 0;
    
    if (transaction.type === 'credit') {
      total += amount;
    } else if (transaction.type === 'transfer' && accountId) {
      // For transfers, if this account is the destination, it's an inflow
      if (transaction.destinationAccountId === accountId) {
        total += amount;
      }
    }
  });

  return total;
}

/**
 * Calculate total outflow (sum of all debit transactions and outgoing transfers)
 * 
 * @param transactions - Array of transactions
 * @param accountId - The account ID being calculated for (required for transfer handling)
 * @param openingDate - Optional opening date to filter transactions (only include transactions on or after this date)
 * @returns Sum of all debit transactions and outgoing transfers
 */
export function calculateTotalOutflow(transactions: Transaction[], accountId?: string, openingDate?: string): number {
  if (!transactions || transactions.length === 0) {
    return 0;
  }

  // Filter by status (only completed and pending, exclude needs_review)
  let relevantTransactions = transactions.filter(t => 
    t.status === 'completed' || t.status === 'pending'
  );

  // Filter by opening date if provided (only include transactions on or after opening date)
  if (openingDate) {
    const openingDateObj = new Date(openingDate);
    openingDateObj.setHours(0, 0, 0, 0);
    
    relevantTransactions = relevantTransactions.filter((transaction) => {
      const transactionDate = new Date(transaction.date);
      transactionDate.setHours(0, 0, 0, 0);
      return transactionDate >= openingDateObj;
    });
  }

  let total = 0;

  relevantTransactions.forEach((transaction) => {
    const amount = Math.abs(transaction.amount) || 0;
    
    if (transaction.type === 'debit') {
      total += amount;
    } else if (transaction.type === 'transfer' && accountId) {
      // For transfers, if this account is the source, it's an outflow
      if (transaction.sourceAccountId === accountId) {
        total += amount;
      }
    }
  });

  return total;
}

